import { Box, Stack } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { PresentationSlideWrapper } from '@/modules/presentation/clientPresentation/PresentationSlideWrapper';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { US_LETTER_ASPECT_RATIO } from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';
import { COLORS } from '@/styles/tokens/colors';
import { PresentationBundleKind } from '@/types/schema';

import { usePresentationTemplateEditorContext } from '../../PresentationTemplateEditor.context';
import { PresentationTemplateCoverSlide } from './PresentationTemplateCoverSlide';
import { PresentationTemplateCustomSlide } from './PresentationTemplateCustomSlide';
import { PresentationTemplateDividerSlide } from './PresentationTemplateDividerSlide';

export function PresentationTemplateSlideEditorBody() {
  const {
    selectedBundleId,
    bundlesFormArray: { fields: bundles },
  } = usePresentationTemplateEditorContext();

  const currentBundle = useMemo(() => {
    if (selectedBundleId === null) return null;
    return bundles.find((b) => b.id === selectedBundleId) || null;
  }, [bundles, selectedBundleId]);

  if (!currentBundle || selectedBundleId === null) {
    return (
      <Stack
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
        direction="column"
      >
        The current slide could not be read.
      </Stack>
    );
  }

  // CoverSlide and DividerSlide render their own PresentationSlide.Slide
  // (so they can pass background props from tenant design config). Other
  // slide types still need the external wrapper.
  let body: ReactNode = null;
  let needsSlideWrapper = true;

  switch (currentBundle.kind) {
    case PresentationBundleKind.CoverSlideBundle:
      body = (
        <PresentationTemplateCoverSlide
          // Key forces remount when switching bundles, ensuring fresh watch subscriptions
          key={selectedBundleId}
          bundleId={selectedBundleId}
        />
      );
      needsSlideWrapper = false;
      break;

    case PresentationBundleKind.CustomPageBundle:
      body = (
        <PresentationTemplateCustomSlide
          // Key forces remount when switching bundles, ensuring fresh watch subscriptions
          key={selectedBundleId}
          bundleId={selectedBundleId}
        />
      );
      break;

    case PresentationBundleKind.DividerBundle:
      body = (
        <PresentationTemplateDividerSlide
          // Key forces remount when switching bundles, ensuring fresh watch subscriptions
          key={selectedBundleId}
          bundleId={selectedBundleId}
        />
      );
      needsSlideWrapper = false;
      break;
    default:
      return (
        <Stack
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          The current slide has no editable attributes.
        </Stack>
      );
  }

  return (
    <Stack
      flex="1 1 auto"
      minWidth={0}
      justifyContent="center"
      alignItems="center"
      p={2}
    >
      {body && (
        // Constrain the slide to the container's height using aspect-ratio.
        // max-height: 100% ensures it doesn't overflow vertically,
        // and aspect-ratio ensures width scales proportionally.
        <Box
          sx={{
            maxHeight: '100%',
            maxWidth: '100%',
            aspectRatio: US_LETTER_ASPECT_RATIO,
          }}
          // these three are necessary to center the slide w/in its container
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <PresentationSlideWrapper
            sx={{
              width: '100%',
              height: '100%',
              // A border reduces the inner dimensions by 2px. ScalableSlide
              // then grows this auto-sized aspect-ratio box to compensate.
              border: 'none',
              outline: `solid 1px ${COLORS.NEUTRAL_GRAY[100]}`,
            }}
          >
            {needsSlideWrapper ? (
              <PresentationSlide.Slide id="slide-editor">
                {body}
              </PresentationSlide.Slide>
            ) : (
              body
            )}
          </PresentationSlideWrapper>
        </Box>
      )}
    </Stack>
  );
}
